import { CartLinesUpdateEvent } from '@shopify/events';

const TRIGGER_SELECTOR = '.quick-add__button--choose';
const MAX_CACHE_ENTRIES = 24;
const cache = new Map();
const pendingRequests = new Map();
const modal = document.getElementById('kv-quick-view-v2');

if (modal instanceof HTMLDialogElement && !window.KervelanQuickViewV2) {
  const content = modal.querySelector('[data-kv-qv-v2-content]');
  const closeButton = modal.querySelector('[data-kv-qv-v2-close]');
  let mediaTransitionTimer = 0;
  let openRequestId = 0;

  const state = {
    phase: 'idle',
    source: '',
    productUrl: '',
    productId: '',
    variantId: '',
    mediaIndex: 0,
    quantity: 1,
    trigger: null,
    product: null,
    selectedOptions: [],
    colorMediaMap: new Map(),
    swatchStyleMap: new Map(),
    purchaseTemplateHTML: '',
    mediaTransitionToken: 0,
  };

  const normalize = (value) => String(value || '').trim().toLowerCase();

  const dispatch = (name, detail = {}) => {
    document.dispatchEvent(
      new CustomEvent(name, {
        detail: {
          productId: state.productId,
          productUrl: state.productUrl,
          source: state.source,
          variantId: state.variantId,
          quantity: state.quantity,
          ...detail,
        },
      })
    );
  };

  const setPhase = (phase) => {
    state.phase = phase;
    modal.dataset.phase = phase;
  };

  const resolveSource = (trigger) => {
    const component = trigger.closest('quick-add-component');
    const explicit =
      trigger.dataset.quickViewSource ||
      component?.dataset.quickViewSource ||
      trigger.closest('[data-quick-view-source]')?.dataset.quickViewSource;
    if (explicit) return explicit;

    if (trigger.closest('.kv-related-products')) return 'product_related_products';
    if (trigger.closest('.kv-featured-items')) return 'home_featured_items';
    if (trigger.closest('.kv-spotlight-carousel')) return 'home_spotlight';
    if (trigger.closest('collection-component')) return 'collection';

    return document.querySelector('#MainContent')?.dataset.template || 'unknown';
  };

  const resolveTriggerPayload = (trigger) => {
    if (!(trigger instanceof HTMLElement)) {
      return { productUrl: '', productId: '', source: 'unknown' };
    }

    const component = trigger.closest('quick-add-component');
    const card = trigger.closest('[data-kv-product-card], product-card');
    const cardLink = card?.querySelector(
      '[data-kv-card-link][href*="/products/"], [ref="productCardLink"][href*="/products/"], a[href*="/products/"]'
    );

    return {
      // Prefer the card link because KERVELAN swatch selection updates it with
      // the currently selected ?variant= while quick-add-component keeps the base URL.
      productUrl:
        (cardLink instanceof HTMLAnchorElement ? cardLink.href : '') ||
        trigger.dataset.productUrl ||
        component?.dataset.productUrl ||
        '',
      productId:
        trigger.dataset.productId ||
        component?.dataset.productId ||
        card?.dataset.productId ||
        '',
      source: resolveSource(trigger),
    };
  };

  const captureCardPresentation = (trigger) => {
    const card = trigger?.closest?.('[data-kv-product-card], product-card');
    const colorMediaMap = new Map();
    const swatchStyleMap = new Map();

    card?.querySelectorAll?.('[data-kervelan-card-swatch]').forEach((button) => {
      if (!(button instanceof HTMLElement)) return;
      const key = normalize(button.dataset.colorName);
      if (!key) return;

      if (button.dataset.imageSrc) {
        colorMediaMap.set(key, button.dataset.imageSrc);
      }

      const visual =
        button.querySelector('.kervelan-swatch-control__swatch, .kervelan-swatch, [style]') ||
        button.firstElementChild;

      if (visual instanceof HTMLElement) {
        const style = getComputedStyle(visual);
        swatchStyleMap.set(key, {
          backgroundColor: style.backgroundColor,
          backgroundImage: style.backgroundImage,
          borderColor: style.borderColor,
        });
      }
    });

    state.colorMediaMap = colorMediaMap;
    state.swatchStyleMap = swatchStyleMap;

    const component = trigger?.closest?.('quick-add-component');
    const purchaseTemplate = component?.querySelector?.('[data-kv-qv-v2-purchase-template]');
    state.purchaseTemplateHTML =
      purchaseTemplate instanceof HTMLTemplateElement ? purchaseTemplate.innerHTML.trim() : '';
  };

  const productRoot = () => content?.querySelector('[data-kv-qv-v2-product]') || null;
  const getSlides = () => [...(productRoot()?.querySelectorAll('[data-kv-qv-v2-slide]') || [])];

  const syncGalleryDots = (index) => {
    productRoot()?.querySelectorAll('[data-kv-qv-v2-dot]').forEach((dot) => {
      if (!(dot instanceof HTMLElement)) return;
      const active = Number.parseInt(dot.dataset.mediaIndex || '', 10) === index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-current', active ? 'true' : 'false');
    });
  };

  const setMediaIndex = (requestedIndex, { animate = state.phase === 'open' } = {}) => {
    const slides = getSlides();
    if (!slides.length) return;

    if (mediaTransitionTimer) {
      window.clearTimeout(mediaTransitionTimer);
      mediaTransitionTimer = 0;
    }

    const index = ((requestedIndex % slides.length) + slides.length) % slides.length;
    const previousIndex = Number.isFinite(state.mediaIndex) ? state.mediaIndex : 0;
    const previousSlide =
      slides.find((slide) => slide.classList.contains('is-active') && !slide.hidden) ||
      slides[previousIndex];
    const nextSlide = slides[index];
    const token = ++state.mediaTransitionToken;

    state.mediaIndex = index;
    syncGalleryDots(index);

    if (!nextSlide) return;

    if (!animate || previousSlide === nextSlide) {
      slides.forEach((slide, slideIndex) => {
        const active = slideIndex === index;
        slide.hidden = !active;
        slide.classList.toggle('is-active', active);
        slide.classList.remove('is-leaving', 'is-entering');
        slide.setAttribute('aria-hidden', String(!active));
      });
      return;
    }

    slides.forEach((slide) => {
      if (slide !== previousSlide && slide !== nextSlide) {
        slide.hidden = true;
        slide.classList.remove('is-active', 'is-leaving', 'is-entering');
        slide.setAttribute('aria-hidden', 'true');
      }
    });

    nextSlide.hidden = false;
    nextSlide.classList.remove('is-active', 'is-leaving');
    nextSlide.classList.add('is-entering');
    nextSlide.setAttribute('aria-hidden', 'false');

    if (previousSlide) {
      previousSlide.hidden = false;
      previousSlide.classList.add('is-active');
      previousSlide.classList.remove('is-entering', 'is-leaving');
      previousSlide.setAttribute('aria-hidden', 'true');
    }

    requestAnimationFrame(() => {
      if (token !== state.mediaTransitionToken) return;

      previousSlide?.classList.remove('is-active');
      previousSlide?.classList.add('is-leaving');

      nextSlide.classList.remove('is-entering');
      nextSlide.classList.add('is-active');

      mediaTransitionTimer = window.setTimeout(() => {
        mediaTransitionTimer = 0;
        if (token !== state.mediaTransitionToken) return;

        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.hidden = !active;
          slide.classList.toggle('is-active', active);
          slide.classList.remove('is-leaving', 'is-entering');
          slide.setAttribute('aria-hidden', String(!active));
        });
      }, 240);
    });
  };

  const shopifyImageUrl = (source, width) => {
    if (!source) return '';

    try {
      const absolute = String(source).startsWith('//') ? `https:${source}` : String(source);
      const url = new URL(absolute, window.location.origin);

      if (url.hostname === 'cdn.shopify.com' || url.hostname.endsWith('.cdn.shopify.com')) {
        url.searchParams.set('width', String(width));
      }

      return url.href;
    } catch (_) {
      return String(source);
    }
  };

  const shopifyImageSrcset = (source) =>
    [480, 740, 1000, 1200]
      .map((width) => `${shopifyImageUrl(source, width)} ${width}w`)
      .join(', ');

  const normalizeImageUrl = (value) => {
    if (!value) return '';
    try {
      const absolute = String(value).startsWith('//') ? `https:${value}` : String(value);
      return new URL(absolute, window.location.origin).pathname;
    } catch (_) {
      return String(value).split('?')[0];
    }
  };

  const findMediaIndexByUrl = (imageUrl) => {
    const target = normalizeImageUrl(imageUrl);
    if (!target) return -1;

    return getSlides().findIndex((slide) => {
      const image = slide.querySelector('img');
      return normalizeImageUrl(image?.src || slide.dataset.imageSrc) === target;
    });
  };

  const findMediaIndexByColor = (color) => {
    const imageUrl = state.colorMediaMap.get(normalize(color));
    return imageUrl ? findMediaIndexByUrl(imageUrl) : -1;
  };

  const getVariantImageUrl = (variant) => {
    const image = variant?.featured_image;
    if (typeof image === 'string') return image;
    return image?.src || image?.url || '';
  };

  const getCurrency = () =>
    window.Shopify?.currency?.active ||
    document.documentElement.dataset.currency ||
    'USD';

  const formatMoney = (cents) => {
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: getCurrency(),
      }).format(amount);
    } catch (_) {
      return `$${amount.toFixed(2)}`;
    }
  };

  const optionDefinitions = (product) => {
    const rawOptions = Array.isArray(product?.options) ? product.options : [];

    return rawOptions.map((rawOption, index) => {
      const name =
        typeof rawOption === 'string'
          ? rawOption
          : rawOption?.name || `Option ${index + 1}`;

      const values = [];
      for (const variant of product?.variants || []) {
        const value = variant?.options?.[index] ?? variant?.[`option${index + 1}`];
        if (value != null && !values.includes(String(value))) values.push(String(value));
      }

      return {
        name,
        index,
        position: index + 1,
        values,
      };
    });
  };

  const resolveVariant = () => {
    const product = state.product;
    if (!product) return null;

    return (
      product.variants?.find((variant) =>
        state.selectedOptions.every(
          (value, index) =>
            String(variant?.options?.[index] ?? variant?.[`option${index + 1}`] ?? '') ===
            String(value ?? '')
        )
      ) || null
    );
  };

  const setAddButtonLabel = (button, label) => {
    if (!(button instanceof HTMLButtonElement)) return;

    let labelNode = button.querySelector('.kv-qv-v2-add__label');
    let progressLabelNode = button.querySelector('.kv-qv-v2-add__label--progress');

    if (!(labelNode instanceof HTMLElement) || !(progressLabelNode instanceof HTMLElement)) {
      labelNode = document.createElement('span');
      labelNode.className = 'kv-qv-v2-add__label';

      progressLabelNode = document.createElement('span');
      progressLabelNode.className = 'kv-qv-v2-add__label--progress';
      progressLabelNode.setAttribute('aria-hidden', 'true');

      button.replaceChildren(labelNode, progressLabelNode);
    }

    labelNode.textContent = label;
    progressLabelNode.textContent = label;
  };

  const setCartProgress = (button, value) => {
    if (!(button instanceof HTMLButtonElement)) return 0;

    const progress = Math.max(0, Math.min(100, Number(value) || 0));
    button.style.setProperty('--kv-qv-v2-cart-progress', `${progress}%`);
    button.dataset.kvQvV2CartProgress = String(Math.round(progress));
    return progress;
  };

  const beginCartProgress = (button) => {
    if (!(button instanceof HTMLButtonElement)) {
      return {
        stage() {},
        complete() {},
        fail() {},
      };
    }

    let progress = setCartProgress(button, 8);
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    requestAnimationFrame(() => {
      progress = setCartProgress(button, Math.max(progress, 18));
    });

    const timer = window.setInterval(() => {
      if (progress >= 70) return;

      const remaining = 70 - progress;
      const increment = Math.max(0.8, remaining * 0.12);
      progress = setCartProgress(button, Math.min(70, progress + increment));
    }, 180);

    const stop = () => window.clearInterval(timer);

    return {
      stage(value) {
        progress = setCartProgress(button, Math.max(progress, value));
      },
      complete() {
        stop();
        progress = setCartProgress(button, 100);
      },
      fail() {
        stop();
        button.classList.remove('is-loading');
        button.removeAttribute('aria-busy');
        setCartProgress(button, 0);
      },
    };
  };

  const syncVariant = ({ syncMedia = true } = {}) => {
    const root = productRoot();
    if (!root) return;

    const variant = resolveVariant();
    const variantInput = root.querySelector('[data-kv-qv-v2-variant-id]');
    const price = root.querySelector('[data-kv-qv-v2-price]');
    const addButton = root.querySelector('[data-kv-qv-v2-add]');
    const buyNowButton = root.querySelector('[data-kv-qv-v2-buy-now]');
    const colorFieldset = [...root.querySelectorAll('[data-option-name]')].find((fieldset) =>
      ['color', 'colour'].includes(normalize(fieldset.dataset.optionName))
    );
    const colorLabel = root.querySelector('[data-kv-qv-v2-color-label]');
    const selectedColor = colorFieldset?.querySelector('input:checked')?.value || '';

    if (colorLabel) colorLabel.textContent = selectedColor;

    if (!variant) {
      state.variantId = '';
      if (variantInput) variantInput.value = '';
      if (addButton) {
        addButton.disabled = true;
        setAddButtonLabel(addButton, 'UNAVAILABLE');
      }
      if (buyNowButton instanceof HTMLButtonElement) buyNowButton.disabled = true;
      return;
    }

    state.variantId = String(variant.id);
    if (variantInput) {
      variantInput.value = state.variantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
    if (price) price.textContent = formatMoney(variant.price);

    if (addButton) {
      addButton.disabled = !variant.available;
      setAddButtonLabel(addButton, variant.available ? 'ADD TO CART' : 'SOLD OUT');
    }
    if (buyNowButton instanceof HTMLButtonElement) {
      buyNowButton.disabled = !variant.available;
    }

    if (syncMedia) {
      let index = findMediaIndexByUrl(getVariantImageUrl(variant));
      if (index < 0) index = findMediaIndexByColor(selectedColor);
      if (index >= 0) setMediaIndex(index);
    }

    dispatch('kv:quick-view:variant-change');
  };

  const syncQuantity = (quantity) => {
    const root = productRoot();
    if (!root) return;

    state.quantity = Math.max(1, Number.parseInt(String(quantity), 10) || 1);

    const output = root.querySelector('[data-kv-qv-v2-quantity-value]');
    const input = root.querySelector('[data-kv-qv-v2-quantity-input]');

    if (output) output.textContent = String(state.quantity);
    if (input) {
      input.value = String(state.quantity);
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  const productAjaxUrl = (productUrl) => {
    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.pathname = `${url.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
    return url;
  };

  const rememberProduct = (key, product) => {
    if (cache.has(key)) cache.delete(key);
    cache.set(key, product);

    while (cache.size > MAX_CACHE_ENTRIES) {
      cache.delete(cache.keys().next().value);
    }

    return product;
  };

  const fetchProduct = async (productUrl) => {
    const url = productAjaxUrl(productUrl);
    const key = url.href;

    if (cache.has(key)) {
      const product = cache.get(key);
      cache.delete(key);
      cache.set(key, product);
      return product;
    }

    if (pendingRequests.has(key)) return pendingRequests.get(key);

    const request = fetch(key, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Quick View V2 product JSON request failed: ${response.status}`);
        }

        const product = await response.json();

        if (!product?.id || !Array.isArray(product?.variants)) {
          throw new Error('Quick View V2 product JSON is incomplete.');
        }

        return rememberProduct(key, product);
      })
      .finally(() => {
        pendingRequests.delete(key);
      });

    pendingRequests.set(key, request);
    return request;
  };

  const prefetchFromTrigger = (trigger) => {
    const payload = resolveTriggerPayload(trigger);
    if (!payload.productUrl) return;
    fetchProduct(payload.productUrl).catch(() => {});
  };

  const makeArrow = (direction) => {
    const button = document.createElement('button');
    button.type = 'button';
    button.className =
      direction < 0
        ? 'kv-qv-v2-gallery__arrow kv-qv-v2-gallery__arrow--prev'
        : 'kv-qv-v2-gallery__arrow kv-qv-v2-gallery__arrow--next';
    button.toggleAttribute('data-kv-qv-v2-prev', direction < 0);
    button.toggleAttribute('data-kv-qv-v2-next', direction > 0);
    button.setAttribute(
      'aria-label',
      direction < 0 ? 'Previous product image' : 'Next product image'
    );

    button.innerHTML =
      direction < 0
        ? `<svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M301.568 536.32c-7.168 0-14.336-2.816-19.968-8.192-11.008-11.008-11.008-28.928 0-39.936L702.464 67.584c11.008-11.008 28.928-11.008 39.936 0 11.008 11.008 11.008 28.928 0 39.936L321.536 528.128c-5.376 5.376-12.8 8.192-19.968 8.192z" fill="#515151"></path><path d="M722.432 964.864c-7.168 0-14.336-2.816-19.968-8.192L281.6 535.808c-11.008-11.008-11.008-28.928 0-39.936 11.008-11.008 28.928-11.008 39.936 0L742.4 916.736c11.008 11.008 11.008 28.928 0 39.936-5.632 5.376-12.8 8.192-19.968 8.192z" fill="#515151"></path></svg>`
        : `<svg viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M782.222222 512L307.2 1012.622222c-14.222222 11.377778-39.822222 14.222222-51.2 2.844445-8.533333-8.533333-14.222222-28.444444 2.844444-45.511111l440.888889-455.111112L258.844444 56.888889S241.777778 28.444444 264.533333 14.222222c19.911111-14.222222 34.133333-8.533333 42.666667 0L782.222222 512z" fill="#515151"></path></svg>`;

    return button;
  };

  const createOptionFieldset = (definition, selectedValue) => {
    const lowerName = normalize(definition.name);
    const isColor = lowerName === 'color' || lowerName === 'colour';
    const isSize = lowerName === 'size';

    const fieldset = document.createElement('fieldset');
    fieldset.className = `kv-qv-v2-option${isColor ? ' kv-qv-v2-option--color' : ''}${
      isSize ? ' kv-qv-v2-option--size' : ''
    }`;
    fieldset.dataset.optionIndex = String(definition.index);
    fieldset.dataset.optionName = definition.name;

    const legend = document.createElement('legend');
    legend.append(`${definition.name}:`);

    if (isColor) {
      legend.append(' ');
      const selected = document.createElement('span');
      selected.setAttribute('data-kv-qv-v2-color-label', '');
      selected.textContent = selectedValue || '';
      legend.append(selected);
    }

    const values = document.createElement('div');
    values.className = 'kv-qv-v2-option__values';

    definition.values.forEach((value) => {
      const label = document.createElement('label');
      label.className = isColor ? 'kv-qv-v2-color' : 'kv-qv-v2-size';
      if (isColor) label.title = value;

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `kv-qv-v2-option-${definition.position}`;
      input.value = value;
      input.checked = String(value) === String(selectedValue);

      if (isColor) {
        const swatch = document.createElement('span');
        swatch.className = 'kv-qv-v2-color__swatch';

        const sourceStyle = state.swatchStyleMap.get(normalize(value));
        if (sourceStyle) {
          if (sourceStyle.backgroundColor) swatch.style.backgroundColor = sourceStyle.backgroundColor;
          if (sourceStyle.backgroundImage && sourceStyle.backgroundImage !== 'none') {
            swatch.style.backgroundImage = sourceStyle.backgroundImage;
          }
          if (sourceStyle.borderColor) swatch.style.borderColor = sourceStyle.borderColor;
        } else if (window.CSS?.supports?.('color', value)) {
          swatch.style.backgroundColor = value;
        }

        const hidden = document.createElement('span');
        hidden.className = 'visually-hidden';
        hidden.textContent = value;

        label.append(input, swatch, hidden);
      } else {
        const text = document.createElement('span');
        text.textContent = value;
        label.append(input, text);
      }

      values.append(label);
    });

    fieldset.append(legend, values);
    return fieldset;
  };

  const createPurchaseForm = () => {
    if (state.purchaseTemplateHTML) {
      const template = document.createElement('template');
      template.innerHTML = state.purchaseTemplateHTML;
      const serverForm = template.content.querySelector('form');

      if (serverForm instanceof HTMLFormElement) {
        serverForm.className = 'kv-qv-v2-form';
        serverForm.dataset.kvQvV2Form = '';
        return serverForm;
      }
    }

    const fallback = document.createElement('form');
    fallback.className = 'kv-qv-v2-form';
    fallback.dataset.kvQvV2Form = '';
    return fallback;
  };

  const renderProductData = (product) => {
    if (!(content instanceof HTMLElement)) return false;

    state.product = product;
    state.productId = String(product.id || '');

    const requestedVariantId = new URL(state.productUrl, window.location.origin).searchParams.get('variant');
    const initialVariant =
      product.variants.find((variant) => String(variant.id) === String(requestedVariantId)) ||
      product.variants.find((variant) => variant.available) ||
      product.variants[0];

    if (!initialVariant) return false;

    state.variantId = String(initialVariant.id);
    state.selectedOptions = [...(initialVariant.options || [])];
    state.quantity = 1;
    state.mediaIndex = 0;

    const root = document.createElement('div');
    root.className = 'kv-qv-v2-product';
    root.dataset.kvQvV2Product = '';
    root.dataset.productId = state.productId;
    root.dataset.productUrl = product.url || state.productUrl;
    root.dataset.productTitle = product.title || '';

    const layout = document.createElement('div');
    layout.className = 'kv-qv-v2-product__layout';

    const gallery = document.createElement('section');
    gallery.className = 'kv-qv-v2-gallery';
    gallery.setAttribute('aria-label', `${product.title || 'Product'} images`);

    const stage = document.createElement('div');
    stage.className = 'kv-qv-v2-gallery__stage';
    stage.dataset.kvQvV2Gallery = '';

    const images = [...new Set((product.images || []).filter(Boolean))];

    if (!images.length && product.featured_image) images.push(product.featured_image);

    images.forEach((src, index) => {
      const slide = document.createElement('figure');
      slide.className = `kv-qv-v2-gallery__slide${index === 0 ? ' is-active' : ''}`;
      slide.dataset.kvQvV2Slide = '';
      slide.dataset.mediaIndex = String(index);
      slide.dataset.imageSrc = src;
      slide.hidden = index !== 0;
      slide.setAttribute('aria-hidden', index === 0 ? 'false' : 'true');

      const image = document.createElement('img');
      image.className = 'kv-qv-v2-gallery__image';
      image.src = shopifyImageUrl(src, 1000);
      image.srcset = shopifyImageSrcset(src);
      image.sizes = '(max-width: 749px) 370px, 540px';
      image.alt = product.title || '';
      image.loading = index === 0 ? 'eager' : 'lazy';
      image.fetchPriority = index === 0 ? 'high' : 'low';
      image.decoding = 'async';
      image.draggable = false;

      slide.append(image);
      stage.append(slide);
    });

    if (images.length > 1) {
      stage.append(makeArrow(-1), makeArrow(1));
    }

    gallery.append(stage);

    if (images.length > 1) {
      const dots = document.createElement('div');
      dots.className = 'kv-qv-v2-gallery__dots';
      dots.setAttribute('aria-label', 'Product image navigation');

      images.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = `kv-qv-v2-gallery__dot${index === 0 ? ' is-active' : ''}`;
        dot.dataset.kvQvV2Dot = '';
        dot.dataset.mediaIndex = String(index);
        dot.setAttribute('aria-label', `View image ${index + 1} of ${images.length}`);
        dot.setAttribute('aria-current', index === 0 ? 'true' : 'false');
        dots.append(dot);
      });

      gallery.append(dots);
    }

    const details = document.createElement('section');
    details.className = 'kv-qv-v2-details';

    const identity = document.createElement('header');
    identity.className = 'kv-qv-v2-identity';

    const brand = document.createElement('div');
    brand.className = 'kv-qv-v2-identity__brand';
    brand.textContent = product.vendor || '';
    brand.hidden = !brand.textContent;

    const title = document.createElement('a');
    title.className = 'kv-qv-v2-identity__title';
    title.href = product.url || state.productUrl;
    title.textContent = product.title || '';

    const price = document.createElement('div');
    price.className = 'kv-qv-v2-identity__price';
    price.dataset.kvQvV2Price = '';
    price.textContent = formatMoney(initialVariant.price);

    identity.append(brand, title, price);

    const form = createPurchaseForm();

    let variantInput = form.querySelector('[data-kv-qv-v2-variant-id], input[name="id"]');
    if (!(variantInput instanceof HTMLInputElement)) {
      variantInput = document.createElement('input');
      variantInput.type = 'hidden';
      variantInput.name = 'id';
    }
    variantInput.value = state.variantId;
    variantInput.dataset.kvQvV2VariantId = '';

    let quantityInput = form.querySelector('[data-kv-qv-v2-quantity-input], input[name="quantity"]');
    if (!(quantityInput instanceof HTMLInputElement)) {
      quantityInput = document.createElement('input');
      quantityInput.type = 'hidden';
      quantityInput.name = 'quantity';
    }
    quantityInput.value = '1';
    quantityInput.dataset.kvQvV2QuantityInput = '';

    let paymentTerms = form.querySelector('[data-kv-qv-v2-payment-terms]');
    if (!(paymentTerms instanceof HTMLElement)) {
      paymentTerms = document.createElement('div');
      paymentTerms.className = 'kv-qv-v2-payment-terms';
      paymentTerms.dataset.kvQvV2PaymentTerms = '';
    }

    const options = document.createElement('div');
    options.className = 'kv-qv-v2-options';
    options.dataset.kvQvV2Options = '';

    const definitions = optionDefinitions(product);
    const presentationOrder = [...definitions].sort((a, b) => {
      const priority = (definition) => {
        const name = normalize(definition.name);
        if (name === 'color' || name === 'colour') return 0;
        if (name === 'size') return 1;
        return 2 + definition.index;
      };
      return priority(a) - priority(b);
    });

    presentationOrder.forEach((definition) => {
      options.append(
        createOptionFieldset(definition, state.selectedOptions[definition.index] || '')
      );
    });

    const quantity = document.createElement('div');
    quantity.className = 'kv-qv-v2-quantity';
    quantity.dataset.kvQvV2Quantity = '';
    quantity.innerHTML =
      '<button type="button" data-kv-qv-v2-quantity-minus aria-label="Decrease quantity">−</button>' +
      '<output data-kv-qv-v2-quantity-value>1</output>' +
      '<button type="button" data-kv-qv-v2-quantity-plus aria-label="Increase quantity">+</button>';

    const note = document.createElement('p');
    note.className = 'kv-qv-v2-note';
    note.textContent = modal.dataset.shippingSummary || '';

    const error = document.createElement('p');
    error.className = 'kv-qv-v2-error';
    error.dataset.kvQvV2Error = '';
    error.setAttribute('role', 'alert');
    error.hidden = true;

    const add = document.createElement('button');
    add.type = 'submit';
    add.className = 'kv-qv-v2-add';
    add.dataset.kvQvV2Add = '';
    add.disabled = !initialVariant.available;
    setCartProgress(add, 0);
    setAddButtonLabel(add, initialVariant.available ? 'ADD TO CART' : 'SOLD OUT');

    const buyNow = document.createElement('button');
    buyNow.type = 'button';
    buyNow.className = 'kv-qv-v2-buy-now';
    buyNow.dataset.kvQvV2BuyNow = '';
    buyNow.textContent = 'BUY NOW';
    buyNow.disabled = !initialVariant.available;

    form.replaceChildren(
      variantInput,
      quantityInput,
      paymentTerms,
      options,
      quantity,
      note,
      error,
      add,
      buyNow
    );

    const viewDetails = document.createElement('a');
    viewDetails.className = 'kv-qv-v2-view-details';
    viewDetails.href = product.url || state.productUrl;
    viewDetails.textContent = 'View details';

    details.append(identity, form, viewDetails);
    layout.append(gallery, details);
    root.append(layout);
    content.replaceChildren(root);

    setMediaIndex(0);
    syncVariant({ syncMedia: true });
    syncQuantity(1);

    return true;
  };

  const releaseProductState = () => {
    content?.replaceChildren();

    state.source = '';
    state.productUrl = '';
    state.productId = '';
    state.variantId = '';
    state.mediaIndex = 0;
    state.quantity = 1;
    state.trigger = null;
    state.product = null;
    state.selectedOptions = [];
    state.colorMediaMap.clear();
    state.swatchStyleMap.clear();
    state.purchaseTemplateHTML = '';
    state.mediaTransitionToken += 1;
  };

  const waitForCloseAnimation = () =>
    new Promise((resolve) => {
      let settled = false;

      const finish = () => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timer);
        modal.removeEventListener('animationend', onAnimationEnd);
        resolve();
      };

      const onAnimationEnd = (event) => {
        if (event.target !== modal) return;
        finish();
      };

      const timer = window.setTimeout(finish, 260);
      modal.addEventListener('animationend', onAnimationEnd);
    });

  const close = async ({ restoreFocus = true } = {}) => {
    if (!modal.open || state.phase === 'closing') return;

    const trigger = state.trigger;
    openRequestId += 1;

    setPhase('closing');
    modal.classList.add('is-closing');

    await waitForCloseAnimation();

    if (mediaTransitionTimer) {
      window.clearTimeout(mediaTransitionTimer);
      mediaTransitionTimer = 0;
    }

    modal.classList.remove('is-closing');
    modal.close();
    document.documentElement.classList.remove('kv-qv-v2-open');
    setPhase('idle');
    dispatch('kv:quick-view:close');

    releaseProductState();

    if (restoreFocus && trigger?.isConnected) {
      trigger.focus?.({ preventScroll: true });
    }
  };

  const open = async ({ productUrl, source = 'manual', trigger = null } = {}) => {
    if (!productUrl || !(content instanceof HTMLElement)) return false;
    if (state.phase === 'loading' || state.phase === 'opening') return false;

    const requestId = ++openRequestId;

    state.source = source;
    state.productUrl = new URL(productUrl, window.location.origin).href;
    state.trigger = trigger instanceof HTMLElement ? trigger : null;
    state.productId = '';
    state.variantId = '';
    state.mediaIndex = 0;
    state.quantity = 1;
    state.product = null;
    state.selectedOptions = [];
    state.purchaseTemplateHTML = '';
    state.mediaTransitionToken = 0;

    captureCardPresentation(state.trigger);

    setPhase('loading');
    content.innerHTML =
      '<div class="kv-qv-v2__loading" data-kv-qv-v2-loading><span class="visually-hidden">Loading product options</span></div>';

    if (!modal.open) {
      document.documentElement.classList.add('kv-qv-v2-open');
      modal.showModal();
    }

    dispatch('kv:quick-view:open');

    try {
      const product = await fetchProduct(state.productUrl);

      if (requestId !== openRequestId || !modal.open || state.phase === 'closing') {
        return false;
      }

      if (!renderProductData(product)) {
        throw new Error('Quick View V2 could not render the product JSON.');
      }

      setPhase('open');
      dispatch('kv:quick-view:loaded');
      return true;
    } catch (error) {
      if (requestId !== openRequestId || !modal.open || state.phase === 'closing') {
        return false;
      }

      console.warn('[KERVELAN QV V2] Unable to load product JSON.', error);
      content.innerHTML =
        '<p class="kv-qv-v2-product--error">Unable to load this product right now.</p>';
      setPhase('error');
      return false;
    }
  };

  const addToCart = async (form) => {
    if (!(form instanceof HTMLFormElement) || !state.variantId || state.phase === 'submitting') return;

    const errorNode = form.querySelector('[data-kv-qv-v2-error]');
    const addButton = form.querySelector('[data-kv-qv-v2-add]');

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }

    setPhase('submitting');
    const progress = beginCartProgress(addButton);
    dispatch('kv:quick-view:add');

    const formData = new FormData(form);
    formData.set('id', state.variantId);
    formData.set('quantity', String(state.quantity));

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((node) => (node instanceof HTMLElement ? node.dataset.sectionId : ''))
      .filter(Boolean);

    if (sectionIds.length) formData.set('sections', [...new Set(sectionIds)].join(','));
    formData.set('sections_url', window.location.pathname);

    const deferred = CartLinesUpdateEvent.createPromise();

    modal.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'quick-view-v2',
        lines: [{ merchandiseId: state.variantId, quantity: state.quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        body: formData,
        headers: { Accept: 'application/json' },
      });

      const result = await response.json();

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Unable to add this item.');
      }

      progress.stage(82);

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });

      if (!cartResponse.ok) throw new Error('Unable to refresh cart.');

      const cart = await cartResponse.json();
      progress.stage(92);

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: state.quantity,
          productId: state.productId,
          source: 'kv-quick-view-v2',
          sections: result.sections,
          didError: false,
        },
      });

      await window.KervelanCartDrawerRuntime?.ensure?.();
      await customElements.whenDefined('theme-drawer');
      progress.stage(97);

      const cartDrawer = document.getElementById('cart-drawer');

      progress.complete();
      await new Promise((resolve) => window.setTimeout(resolve, 140));

      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
      }

      await close({ restoreFocus: false });
    } catch (error) {
      deferred.reject(error);
      console.warn('[KERVELAN QV V2] Add to cart failed.', error);

      if (errorNode) {
        errorNode.textContent = error?.message || 'Unable to add this item.';
        errorNode.hidden = false;
      }

      progress.fail();
      setPhase('open');

      const currentVariant = resolveVariant();
      if (addButton) {
        addButton.disabled = !(currentVariant?.available);
        setAddButtonLabel(
          addButton,
          currentVariant?.available ? 'ADD TO CART' : currentVariant ? 'SOLD OUT' : 'UNAVAILABLE'
        );
      }
    }
  };

  const buyNow = async (form, button) => {
    if (
      !(form instanceof HTMLFormElement) ||
      !(button instanceof HTMLButtonElement) ||
      !state.variantId ||
      state.phase === 'submitting'
    ) {
      return;
    }

    const variant = resolveVariant();
    const errorNode = form.querySelector('[data-kv-qv-v2-error]');
    const storefrontDomain = modal.dataset.storefrontDomain || '';
    const storefrontAccessToken = modal.dataset.storefrontAccessToken || '';

    if (!variant?.available || !storefrontDomain || !storefrontAccessToken) return;

    if (errorNode) {
      errorNode.hidden = true;
      errorNode.textContent = '';
    }

    setPhase('submitting');
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'LOADING…';
    dispatch('kv:quick-view:buy-now');

    const quantity = Math.max(1, Number.parseInt(String(state.quantity), 10) || 1);
    const merchandiseId = `gid://shopify/ProductVariant/${state.variantId}`;
    const endpoint = `https://${storefrontDomain}/api/2026-07/graphql.json`;
    const query = `
      mutation CartCreate($input: CartInput!) {
        cartCreate(input: $input) {
          cart {
            checkoutUrl
          }
          userErrors {
            field
            message
          }
        }
      }
    `;

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
        },
        credentials: 'omit',
        body: JSON.stringify({
          query,
          variables: {
            input: {
              lines: [{ merchandiseId, quantity }],
            },
          },
        }),
      });

      const payload = await response.json();
      const cartCreate = payload?.data?.cartCreate;
      const checkoutUrl = cartCreate?.cart?.checkoutUrl;
      const userError = cartCreate?.userErrors?.[0]?.message;
      const graphQLError = payload?.errors?.[0]?.message;

      if (!response.ok || !checkoutUrl) {
        throw new Error(userError || graphQLError || 'Unable to start checkout.');
      }

      window.location.assign(checkoutUrl);
    } catch (error) {
      console.warn('[KERVELAN QV V2] Independent checkout failed.', error);

      if (errorNode) {
        errorNode.textContent = error?.message || 'Unable to start checkout.';
        errorNode.hidden = false;
      }

      button.removeAttribute('aria-busy');
      button.textContent = 'BUY NOW';
      button.disabled = !resolveVariant()?.available;
      setPhase('open');
    }
  };
  const getModalScrollContainer = (target) => {
    if (!(target instanceof Element)) return null;

    const candidate = target.closest('.kv-qv-v2__content');
    if (!(candidate instanceof HTMLElement)) return null;

    const overflowY = getComputedStyle(candidate).overflowY;
    if (!/(auto|scroll)/.test(overflowY)) return null;
    if (candidate.scrollHeight <= candidate.clientHeight + 1) return null;

    return candidate;
  };

  const shouldContainScroll = (target, deltaY) => {
    const scrollContainer = getModalScrollContainer(target);
    if (!scrollContainer) return true;
    if (deltaY === 0) return false;

    const atTop = scrollContainer.scrollTop <= 0;
    const atBottom =
      scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 1;

    if (deltaY < 0 && atTop) return true;
    if (deltaY > 0 && atBottom) return true;
    return false;
  };

  modal.addEventListener(
    'wheel',
    (event) => {
      if (!modal.open) return;
      if (shouldContainScroll(event.target, event.deltaY)) event.preventDefault();
    },
    { passive: false }
  );

  let lastTouchX = null;
  let lastTouchY = null;

  modal.addEventListener(
    'touchstart',
    (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;
    },
    { passive: true }
  );

  modal.addEventListener(
    'touchmove',
    (event) => {
      if (!modal.open) return;

      const touch = event.touches[0];
      if (!touch || lastTouchX == null || lastTouchY == null) return;

      const deltaX = lastTouchX - touch.clientX;
      const deltaY = lastTouchY - touch.clientY;
      lastTouchX = touch.clientX;
      lastTouchY = touch.clientY;

      if (Math.abs(deltaY) <= Math.abs(deltaX)) return;
      if (shouldContainScroll(event.target, deltaY)) event.preventDefault();
    },
    { passive: false }
  );

  modal.addEventListener(
    'touchend',
    () => {
      lastTouchX = null;
      lastTouchY = null;
    },
    { passive: true }
  );

  content?.addEventListener('click', (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const buyNowButton = target.closest('[data-kv-qv-v2-buy-now]');
    if (buyNowButton instanceof HTMLButtonElement) {
      event.preventDefault();
      const form = buyNowButton.closest('[data-kv-qv-v2-form]');
      buyNow(form, buyNowButton);
      return;
    }

    const dot = target.closest('[data-kv-qv-v2-dot]');
    if (dot instanceof HTMLElement) {
      event.preventDefault();
      const index = Number.parseInt(dot.dataset.mediaIndex || '', 10);
      if (Number.isFinite(index)) setMediaIndex(index);
      return;
    }

    if (target.closest('[data-kv-qv-v2-prev]')) {
      event.preventDefault();
      setMediaIndex(state.mediaIndex - 1);
      return;
    }

    if (target.closest('[data-kv-qv-v2-next]')) {
      event.preventDefault();
      setMediaIndex(state.mediaIndex + 1);
      return;
    }

    if (target.closest('[data-kv-qv-v2-quantity-minus]')) {
      event.preventDefault();
      syncQuantity(state.quantity - 1);
      return;
    }

    if (target.closest('[data-kv-qv-v2-quantity-plus]')) {
      event.preventDefault();
      syncQuantity(state.quantity + 1);
    }
  });

  content?.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const fieldset = input.closest('[data-option-index]');
    if (!(fieldset instanceof HTMLElement)) return;

    const index = Number.parseInt(fieldset.dataset.optionIndex || '', 10);
    if (!Number.isFinite(index)) return;

    state.selectedOptions[index] = input.value;
    syncVariant();
  });

  content?.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-kv-qv-v2-form]')) return;

    const submitter = event.submitter;
    if (!(submitter instanceof HTMLElement) || !submitter.matches('[data-kv-qv-v2-add]')) return;

    event.preventDefault();
    addToCart(form);
  });

  closeButton?.addEventListener('click', () => close());

  modal.addEventListener('cancel', (event) => {
    event.preventDefault();
    close();
  });

  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });

  document.addEventListener(
    'click',
    (event) => {
      const trigger =
        event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;

      if (!(trigger instanceof HTMLElement)) return;
      if (trigger.closest('#kv-quick-view-v2')) return;

      const payload = resolveTriggerPayload(trigger);
      if (!payload.productUrl) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      open({
        productUrl: payload.productUrl,
        source: payload.source,
        trigger,
      });
    },
    true
  );

  for (const eventName of ['pointerover', 'focusin', 'touchstart']) {
    document.addEventListener(
      eventName,
      (event) => {
        const trigger =
          event.target instanceof Element ? event.target.closest(TRIGGER_SELECTOR) : null;

        if (!(trigger instanceof HTMLElement)) return;
        prefetchFromTrigger(trigger);
      },
      { capture: true, passive: eventName !== 'focusin' }
    );
  }

  window.addEventListener(
    'pagehide',
    () => {
      openRequestId += 1;

      if (mediaTransitionTimer) {
        window.clearTimeout(mediaTransitionTimer);
        mediaTransitionTimer = 0;
      }

    },
    { once: true }
  );

  window.KervelanQuickViewV2 = {
    open,
    close,
    get state() {
      return {
        phase: state.phase,
        source: state.source,
        productUrl: state.productUrl,
        productId: state.productId,
        variantId: state.variantId,
        mediaIndex: state.mediaIndex,
        quantity: state.quantity,
        selectedOptions: [...state.selectedOptions],
      };
    },
    clearCache() {
      cache.clear();
    },
  };
}

